<app-catalog-page-card title="BONDS CLONING" [wide]="true">
  <div class="bonds-cloning-header">
    <p class="text-sm text-secondary-foreground">
      Clone bond setup from a source trade into a target trade using the backend cloning rules.
    </p>
  </div>

  <div class="bonds-cloning-stepper" aria-label="Bonds cloning steps">
    <div class="bonds-cloning-step" [class.active]="isCurrentStep(1)">
      <span class="bonds-cloning-step-index">1</span>
      <span>Select source</span>
    </div>
    <div class="bonds-cloning-step" [class.active]="isCurrentStep(2)">
      <span class="bonds-cloning-step-index">2</span>
      <span>Select target</span>
    </div>
    <div class="bonds-cloning-step" [class.active]="isCurrentStep(3)">
      <span class="bonds-cloning-step-index">3</span>
      <span>Confirm and execute</span>
    </div>
  </div>

  @if (isCurrentStep(1)) {
    <section class="grid gap-4">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search source bonds..."
          class="kt-input sm:w-72"
          [attr.data-kt-datatable-search]="sourceDatatableSelector"
        />
        <button type="button" class="kt-btn kt-btn-outline kt-btn-icon" aria-label="Refresh source bonds" (click)="loadSources()">
          <i class="ki-filled ki-arrows-circle"></i>
        </button>
      </div>

      @if (isLoadingSources()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading source bonds">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading...</span>
        </div>
      } @else if (sourceError()) {
        <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
          <span>{{ sourceError() }}</span>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadSources()">Retry</button>
        </div>
      } @else {
        <div
          id="kt_bonds_cloning_source_datatable"
          class="kt-card-table bonds-cloning-table-compact"
          data-kt-datatable="true"
          data-kt-datatable-page-size="20"
          data-kt-datatable-state-save="false"
          data-kt-datatable-state-namespace="bonds-cloning-source-datatable"
          (click)="onSourceTableActionClick($event)"
        >
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table" data-kt-datatable-table="true">
              <thead>
                <tr>
                  <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="securityType"><span class="kt-table-col"><span class="kt-table-col-label">Security Type</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="settlementDate"><span class="kt-table-col"><span class="kt-table-col-label">Settlement Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="maturityDate"><span class="kt-table-col"><span class="kt-table-col-label">Maturity Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="currency"><span class="kt-table-col"><span class="kt-table-col-label">Currency</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="notional"><span class="kt-table-col"><span class="kt-table-col-label">Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="coupon"><span class="kt-table-col"><span class="kt-table-col-label">Coupon</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="marketType"><span class="kt-table-col"><span class="kt-table-col-label">Market Type</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeStatus"><span class="kt-table-col"><span class="kt-table-col-label">Trade Status</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="us3mlCost"><span class="kt-table-col"><span class="kt-table-col-label">US3mL Cost</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="actions" data-kt-datatable-column-sort="false" class="text-end">Select</th>
                </tr>
              </thead>
              <tbody>
                @if (sources().length === 0) {
                  <tr><td colspan="14" class="text-center text-secondary-foreground py-6">No source bonds available.</td></tr>
                } @else {
                  @for (row of sources(); track row.tradeId) {
                    <tr [class.bonds-cloning-row-matured]="row.matured" [class.bonds-cloning-row-selected]="selectedSource()?.tradeId === row.tradeId">
                      <td>{{ row.dealId || '-' }}</td>
                      <td>
                        <a class="text-primary hover:underline" [routerLink]="['/bond-detail', row.tradeId]" target="_blank" rel="noopener">
                          {{ row.tradeId }}
                        </a>
                      </td>
                      <td>
                        @if (row.securityId) {
                          <a class="text-primary hover:underline" [routerLink]="['/bond-definition', row.securityId]" target="_blank" rel="noopener">
                            {{ row.securityId }}
                          </a>
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ row.securityType || '-' }}</td>
                      <td>{{ formatDate(row.tradeDate) }}</td>
                      <td>{{ formatDate(row.settlementDate) }}</td>
                      <td>{{ formatDate(row.maturityDate) }}</td>
                      <td>{{ row.currency || '-' }}</td>
                      <td class="text-end">{{ formatNumber(row.notional, 2) }}</td>
                      <td class="text-end">{{ formatNumber(row.coupon, 4) }}</td>
                      <td>{{ row.marketType || '-' }}</td>
                      <td>{{ row.tradeStatus || '-' }}</td>
                      <td class="text-end">{{ formatNumber(row.us3mlCost, 2) }}</td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary bonds-cloning-select-icon"
                          data-action="select-source-bond"
                          [attr.data-source-trade-id]="row.tradeId"
                          aria-label="Select source bond"
                          title="Select source bond"
                        >
                          <i class="ki-filled ki-check-circle" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
            <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
            <div class="kt-datatable-length flex items-center gap-3">
              <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
              <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
            </div>
          </div>
        </div>
      }
    </section>
  }

  @if (isCurrentStep(2)) {
    <section class="grid gap-4">
      <div class="bonds-cloning-summary-grid">
        <div class="kt-card p-4">
          <h3 class="text-sm font-semibold text-mono mb-1">Selected source</h3>
          <p class="text-sm">{{ selectedSource()?.tradeId || '-' }} · {{ selectedSource()?.dealId || '-' }}</p>
        </div>
        <div class="kt-card p-4">
          <h3 class="text-sm font-semibold text-mono mb-1">Selected target</h3>
          <p class="text-sm">{{ selectedTarget()?.tradeId || '-' }} · {{ selectedTarget()?.dealId || '-' }}</p>
        </div>
      </div>

      <div class="flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search target bonds..."
          class="kt-input sm:w-72"
          [attr.data-kt-datatable-search]="targetDatatableSelector"
        />
        <div class="inline-flex items-center gap-2">
          <button type="button" class="kt-btn kt-btn-outline" (click)="onBackToSourceStep()">Back</button>
          <button type="button" class="kt-btn kt-btn-outline kt-btn-icon" aria-label="Refresh target bonds" (click)="loadTargets()">
            <i class="ki-filled ki-arrows-circle"></i>
          </button>
        </div>
      </div>

      @if (isLoadingTargets()) {
        <div class="catalog-loading" role="status" aria-live="polite" aria-label="Loading target bonds">
          <span class="catalog-loading-spinner" aria-hidden="true"></span>
          <span class="catalog-loading-text">Loading...</span>
        </div>
      } @else if (targetError()) {
        <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
          <span>{{ targetError() }}</span>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="loadTargets()">Retry</button>
        </div>
      } @else {
        <div
          id="kt_bonds_cloning_target_datatable"
          class="kt-card-table bonds-cloning-table-compact"
          data-kt-datatable="true"
          data-kt-datatable-page-size="20"
          data-kt-datatable-state-save="false"
          data-kt-datatable-state-namespace="bonds-cloning-target-datatable"
          (click)="onTargetTableActionClick($event)"
        >
          <div class="kt-table-wrapper kt-scrollable">
            <table class="kt-table" data-kt-datatable-table="true">
              <thead>
                <tr>
                  <th scope="col" data-kt-datatable-column="dealId"><span class="kt-table-col"><span class="kt-table-col-label">Deal Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeId"><span class="kt-table-col"><span class="kt-table-col-label">Trade Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="securityId"><span class="kt-table-col"><span class="kt-table-col-label">Security Id</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="securityType"><span class="kt-table-col"><span class="kt-table-col-label">Security Type</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeDate"><span class="kt-table-col"><span class="kt-table-col-label">Trade Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="settlementDate"><span class="kt-table-col"><span class="kt-table-col-label">Settlement Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="maturityDate"><span class="kt-table-col"><span class="kt-table-col-label">Maturity Date</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="currency"><span class="kt-table-col"><span class="kt-table-col-label">Currency</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="notional"><span class="kt-table-col"><span class="kt-table-col-label">Notional</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="coupon"><span class="kt-table-col"><span class="kt-table-col-label">Coupon</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="marketType"><span class="kt-table-col"><span class="kt-table-col-label">Market Type</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="tradeStatus"><span class="kt-table-col"><span class="kt-table-col-label">Trade Status</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="us3mlCost"><span class="kt-table-col"><span class="kt-table-col-label">US3mL Cost</span><span class="kt-table-col-sort"></span></span></th>
                  <th scope="col" data-kt-datatable-column="actions" data-kt-datatable-column-sort="false" class="text-end">Select</th>
                </tr>
              </thead>
              <tbody>
                @if (targets().length === 0) {
                  <tr><td colspan="14" class="text-center text-secondary-foreground py-6">No target bonds available for this source.</td></tr>
                } @else {
                  @for (row of targets(); track row.tradeId) {
                    <tr [class.bonds-cloning-row-matured]="row.matured" [class.bonds-cloning-row-selected]="selectedTarget()?.tradeId === row.tradeId">
                      <td>{{ row.dealId || '-' }}</td>
                      <td>
                        <a class="text-primary hover:underline" [routerLink]="['/bond-detail', row.tradeId]" target="_blank" rel="noopener">
                          {{ row.tradeId }}
                        </a>
                      </td>
                      <td>
                        @if (row.securityId) {
                          <a class="text-primary hover:underline" [routerLink]="['/bond-definition', row.securityId]" target="_blank" rel="noopener">
                            {{ row.securityId }}
                          </a>
                        } @else {
                          -
                        }
                      </td>
                      <td>{{ row.securityType || '-' }}</td>
                      <td>{{ formatDate(row.tradeDate) }}</td>
                      <td>{{ formatDate(row.settlementDate) }}</td>
                      <td>{{ formatDate(row.maturityDate) }}</td>
                      <td>{{ row.currency || '-' }}</td>
                      <td class="text-end">{{ formatNumber(row.notional, 2) }}</td>
                      <td class="text-end">{{ formatNumber(row.coupon, 4) }}</td>
                      <td>{{ row.marketType || '-' }}</td>
                      <td>{{ row.tradeStatus || '-' }}</td>
                      <td class="text-end">{{ formatNumber(row.us3mlCost, 2) }}</td>
                      <td class="text-end">
                        <button
                          type="button"
                          class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary bonds-cloning-select-icon"
                          data-action="select-target-bond"
                          [attr.data-target-trade-id]="row.tradeId"
                          aria-label="Select target bond"
                          title="Select target bond"
                        >
                          <i class="ki-filled ki-check-circle" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
          <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
            <div data-kt-datatable-info="true" class="kt-datatable-info text-sm text-secondary-foreground"></div>
            <div class="kt-datatable-length flex items-center gap-3">
              <select data-kt-datatable-size="true" class="kt-select kt-select-sm w-20"></select>
              <div data-kt-datatable-pagination="true" class="kt-datatable-pagination flex items-center gap-2"></div>
            </div>
          </div>
        </div>
      }
    </section>
  }

  @if (isCurrentStep(3)) {
    <section class="grid gap-4">
      <div class="kt-card p-6 lg:p-8 bonds-cloning-confirmation-card">
        <h2 class="text-xl lg:text-2xl font-semibold text-mono">
          You are about to clone information between these bonds. Do you want to proceed?
        </h2>
        <div class="mt-4 bonds-cloning-confirmation-grid">
          <div class="kt-card p-4">
            <h3 class="text-sm font-semibold text-mono mb-2">Source bond</h3>
            <p class="text-sm text-secondary-foreground">Deal ID: <strong>{{ selectedSource()?.dealId || '-' }}</strong></p>
            <p class="text-sm text-secondary-foreground mt-1">Trade ID: <strong>{{ selectedSource()?.tradeId || '-' }}</strong></p>
          </div>
          <div class="kt-card p-4">
            <h3 class="text-sm font-semibold text-mono mb-2">Target bond</h3>
            <p class="text-sm text-secondary-foreground">Deal ID: <strong>{{ selectedTarget()?.dealId || '-' }}</strong></p>
            <p class="text-sm text-secondary-foreground mt-1">Trade ID: <strong>{{ selectedTarget()?.tradeId || '-' }}</strong></p>
          </div>
        </div>
        <p class="mt-1 text-base text-secondary-foreground">
          Resolved mode: <strong>{{ resolvedCloneType() }}</strong>. All FRIDA information currently stored on the target bond will be overwritten.
        </p>
      </div>

      @if (executeError()) {
        <div class="kt-alert kt-alert-destructive">{{ executeError() }}</div>
      }

      @if (executeResult(); as execution) {
        <div class="kt-alert kt-alert-success">
          <div class="kt-alert-title"><strong>Bond cloning completed.</strong></div>
          <div class="kt-alert-text">Target: {{ execution.targetTradeId }} · Executed at: {{ formatDateTime(execution.executedAtUtc) }}</div>
          <div class="mt-2">
            <a class="text-primary hover:underline" [routerLink]="['/bond-detail', execution.targetTradeId]" target="_blank" rel="noopener">
              Open target bond details
            </a>
          </div>
        </div>
      }

      <div class="flex items-center justify-center gap-2">
        <button type="button" class="kt-btn kt-btn-outline" (click)="onBackToTargetStep()">Back</button>
        <button type="button" class="kt-btn kt-btn-primary" (click)="onExecute()" [disabled]="isExecuting()">
          @if (!isExecuting()) { Proceed } @else { Processing... }
        </button>
      </div>
    </section>
  }
</app-catalog-page-card>
